// stores/useSubjectsStore.ts
import { create } from 'zustand';
import apiClient from '../services/axiosInterceptor';

interface SubjectItem {
  subject_id: string;
  subject_name: string;
  class_id: string;
  subject_type: 'regular' | 'optional' | 'combined';
  main_subject_id: string | null;
  status: 'active' | 'inactive';
  created_at: string;
  updated_at: string;
  class_name: string;
}

interface SubjectsByType {
  regular: SubjectItem[];
  optional: SubjectItem[];
  combined: SubjectItem[];
}

// ✅ New: Cache subjects per class
interface ClassSubjectsCache {
  [classId: number]: {
    class_name: string;
    subjects: SubjectItem[];
  };
}

interface SubjectsState {
  class_id: string | null;
  class_name: string | null;
  subjects: SubjectsByType;
  loading: boolean;
  error: string | null;

  // ✅ New: Cache for all classes' subjects
  subjectsByClass: ClassSubjectsCache;
  loadingAll: boolean;
  errorAll: string | null;

  fetchSubjects: (classId: number, page?: number, limit?: number) => Promise<void>;

  // ✅ New: Fetch subjects for ALL classes at once
  fetchAllSubjectsForClasses: (classIds: number[]) => Promise<void>;
}

export const useSubjectsStore = create<SubjectsState>((set, get) => ({
  class_id: null,
  class_name: null,
  subjects: {
    regular: [],
    optional: [],
    combined: [],
  },
  loading: false,
  error: null,

  // ✅ New state
  subjectsByClass: {},
  loadingAll: false,
  errorAll: null,

  fetchSubjects: async (classId, page = 1, limit = 25) => {
    set({ loading: true, error: null });

    try {
      const response = await apiClient.get(
        `subjects?class_id=${classId}&page=${page}&limit=${limit}&flat=true`
      );

      const { class_id, class_name, subjects } = response.data;

      const allSubjects: SubjectItem[] = Array.isArray(subjects) ? subjects : [];

      const safeSubjects: SubjectsByType = {
        regular:  allSubjects.filter(s => s.subject_type === 'regular'),
        optional: allSubjects.filter(s => s.subject_type === 'optional'),
        combined: allSubjects.filter(s => s.subject_type === 'combined'),
      };

      // ✅ Also update the cache for this class
      set((state) => ({
        class_id: class_id ?? null,
        class_name: class_name ?? null,
        subjects: safeSubjects,
        loading: false,
        subjectsByClass: {
          ...state.subjectsByClass,
          [classId]: {
            class_name: class_name ?? '',
            subjects: allSubjects,
          },
        },
      }));
    } catch (err: any) {
      set({
        loading: false,
        error: err.response?.data?.message || err.message || 'Failed to load subjects',
      });
    }
  },

  // ✅ New: Fetch subjects for ALL classes (used for CSV download)
  fetchAllSubjectsForClasses: async (classIds: number[]) => {
    set({ loadingAll: true, errorAll: null });

    try {
      const promises = classIds.map(async (classId) => {
        try {
          const response = await apiClient.get(
            `subjects?class_id=${classId}&page=1&limit=100&flat=true`
          );
          const { class_name, subjects } = response.data;
          const allSubjects: SubjectItem[] = Array.isArray(subjects) ? subjects : [];
          return {
            classId,
            class_name: class_name ?? '',
            subjects: allSubjects,
          };
        } catch {
          return {
            classId,
            class_name: '',
            subjects: [],
          };
        }
      });

      const results = await Promise.all(promises);

      const newCache: ClassSubjectsCache = {};
      results.forEach((result) => {
        newCache[result.classId] = {
          class_name: result.class_name,
          subjects: result.subjects,
        };
      });

      set((state) => ({
        loadingAll: false,
        subjectsByClass: {
          ...state.subjectsByClass,
          ...newCache,
        },
      }));
    } catch (err: any) {
      set({
        loadingAll: false,
        errorAll: err.response?.data?.message || err.message || 'Failed to load all subjects',
      });
    }
  },
}));